import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { formatLocalDate, formatTime } from '../../utils/dateFormat';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useCourseDetailModal, CourseDetailModal } from '../../components/CourseDetailModal';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Calendar, Clock, MapPin, Users, ShoppingCart,
  LogIn, UserPlus, CheckCircle2, Tag, Percent, Gift, Star, Baby, Loader2,
  Check, X, Package, Info, ChevronRight,
  FlaskConical, Code, Cog, Calculator, Bot
} from 'lucide-react';

// Refactored components
import { AuthToggle } from '../../components/registration/AuthToggle';
import { GuestCheckoutForm } from '../../components/registration/GuestCheckoutForm';
import { PricingSummary } from '../../components/registration/PricingSummary';
import { DaySelector } from '../../components/registration/DaySelector';
import { LocationPicker } from '../../components/registration/LocationPicker';
import { ExtendedCareSelector } from '../../components/registration/ExtendedCareSelector';
import { CampDetailsCard } from '../../components/registration/CampDetailsCard';
import DobPicker from '../../components/registration/DobPicker';
import PhoneVerificationModal from '../../components/auth/PhoneVerificationModal';
import useAccountCreation from '../../hooks/useAccountCreation';
import { usePricing } from '../../hooks/usePricing';
import { useCampDetails } from '../../hooks/useCampDetails';
import { computePricing } from '../../utils/campPricing';
import { trackAddToCart } from '../../utils/ga4';

const CATEGORY_CONFIG = {
  science: { label: 'Science', color: 'bg-rose-500' },
  coding: { label: 'Technology', color: 'bg-blue-500' },
  robotics: { label: 'Robotics', color: 'bg-orange-500' },
  engineering: { label: 'Engineering', color: 'bg-emerald-500' },
  math: { label: 'Math', color: 'bg-purple-500' },
};

// Weekly STEM rotation theme — used to render a color-coded gradient banner
// on each day card so the rhythm matches the /summer-camps schedule layout.
// { dayNumber: { label, gradient, iconName } }. Icons resolved inline.
const DAY_THEME_REG = {
  1: { label: 'Science',     gradient: 'from-rose-500 to-pink-400',      Icon: FlaskConical },
  2: { label: 'Technology',  gradient: 'from-blue-500 to-indigo-400',    Icon: Code },
  3: { label: 'Engineering', gradient: 'from-emerald-500 to-teal-400',   Icon: Cog },
  4: { label: 'Math',        gradient: 'from-purple-500 to-violet-400',  Icon: Calculator },
  5: { label: 'Robotics',    gradient: 'from-orange-500 to-amber-400',   Icon: Bot },
};

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// Oswego Community Unit School District 308 schools.
// Used to verify student eligibility when a parent selects Bednarcik / Plank Junior High —
// both are partnership locations reserved for in-district families.
// Source: https://sd308.org
const SD308_SCHOOLS = [
  // Junior high schools
  'Bednarcik Junior High School',
  'Plank Junior High School',
  'Thompson Junior High School',
  'Traughber Junior High School',
  'Murphy Junior High School',
  // High schools
  'Oswego High School',
  'Oswego East High School',
  // Elementary schools
  'Boulder Hill Elementary School',
  'Churchill Elementary School',
  'East View Elementary School',
  'Fox Chase Elementary School',
  'Grande Park Elementary School',
  'Homestead Elementary School',
  'Hunt Club Elementary School',
  'The Lakewood Creek Elementary School',
  'Long Beach Elementary School',
  'Old Post Elementary School',
  'Prairie Point Elementary School',
  'Southbury Elementary School',
  'Wheatlands Elementary School',
  'Wolf\u2019s Crossing Elementary School',
];

export default function RegistrationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, api, refreshUser } = useAuth();
  const { refreshCart, cart, itemCount } = useCart();
  const courseDetailModal = useCourseDetailModal();

  // Get type and ID from URL
  const type = searchParams.get('type'); // 'camp' or 'course'
  const id = searchParams.get('id');
  const sectionId = searchParams.get('section');
  const preSelectedDay = searchParams.get('day');

  // ───────────────────────────────────────────────────────────────────
  // Inline-registration redirect (Feb 2026)
  // ───────────────────────────────────────────────────────────────────
  // Camp registration is now an *inline panel* on /summer-camps instead of
  // a standalone page. Any deep link / email / SMS pointing at /register?
  // type=camp&... is forwarded to /summer-camps with the same intent
  // expressed as autoOpenCamp/section/day/session/preset query params.
  //
  // Course registration still uses this page as before.
  useEffect(() => {
    if (type !== 'camp' || !id) return;
    const fwd = new URLSearchParams();
    fwd.set('autoOpenCamp', id);
    if (sectionId) fwd.set('section', sectionId);
    const day = searchParams.get('day');
    const session = searchParams.get('session');
    const preset = searchParams.get('preset');
    const grade = searchParams.get('grade');
    const from = searchParams.get('from');
    if (day) fwd.set('day', day);
    if (session) fwd.set('session', session);
    if (preset) fwd.set('preset', preset);
    if (grade) fwd.set('grade', grade);
    if (from) fwd.set('from', from);
    navigate(`/summer-camps?${fwd.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  // State
  const [loading, setLoading] = useState(true);
  const [itemData, setItemData] = useState(null); // camp or course data
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [selectedDays, setSelectedDays] = useState([]); // Legacy: [1, 2, 3]
  const [selectedSessions, setSelectedSessions] = useState({}); // New: {1: 'am', 2: 'pm', 3: 'full_day'}
  const [extendedCare, setExtendedCare] = useState('none');
  // SD 308 school verification — required when registering at Bednarcik/Plank (restricted sections).
  // Stored as the school NAME string so admins can read it directly from registration records.
  const [sd308School, setSd308School] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [allWeeks, setAllWeeks] = useState([]); // For Jump to Week navigation
  // Post add-to-cart confirmation modal — gives parents a chance to bundle
  // additional weeks before being whisked off to /cart. Boosts multi-week conversions.
  const [showAddedConfirm, setShowAddedConfirm] = useState(false);
  // Snapshot of what was just added — keeps an inline "✓ N sessions added" banner
  // visible on the page after the dialog closes so parents understand the
  // right-sidebar's "$0" is the NEXT selection (not the cart total).
  const [lastAdded, setLastAdded] = useState(null); // { count, total }


  // Phone verification state (Twilio Verify gate before Add-to-Cart)
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);
  // Session-level flags so we don't re-prompt after success/skip within the same flow.
  const [phoneVerifiedThisSession, setPhoneVerifiedThisSession] = useState(false);
  const [phoneSkippedThisSession, setPhoneSkippedThisSession] = useState(false);
  // After phone verification/skip completes, we flip this flag so a useEffect
  // (with a *fresh* handleAddToCart closure) can resume the cart flow.
  // Using setTimeout(handleAddToCart) here would capture a STALE closure where
  // phoneVerifiedThisSession=false, causing the verification modal to reopen
  // infinitely — presenting as a "dark overlay hang" after the user enters OTP.
  const [resumeCheckoutAfterVerify, setResumeCheckoutAfterVerify] = useState(false);


  // Toggle session selection (AM/PM/Full-day)
  // Checkbox-style session toggle (Feb 2026 rewrite per PDF feedback).
  //
  // AM and PM are independent checkboxes. Selecting both for the same day
  // collapses to 'full_day'. Unchecking one from a full_day keeps the other.
  //
  // State shape stays `{dayNum: 'am'|'pm'|'full_day'}` so existing cart
  // payload / backend / display code is untouched. Tiered pricing resolver
  // already counts full_day as 2 sessions — cost-equivalent to AM+PM.
  const toggleSession = (dayNumber, sessionType) => {
    setSelectedSessions(prev => {
      const newSessions = { ...prev };
      const current = newSessions[dayNumber];

      if (sessionType === 'full_day') {
        // Back-compat: direct full-day toggle still works.
        if (current === 'full_day') delete newSessions[dayNumber];
        else newSessions[dayNumber] = 'full_day';
      } else if (sessionType === 'am') {
        if (current === 'am') delete newSessions[dayNumber];
        else if (current === 'pm') newSessions[dayNumber] = 'full_day';  // AM+PM → full day
        else if (current === 'full_day') newSessions[dayNumber] = 'pm';  // uncheck AM from full → keep PM
        else newSessions[dayNumber] = 'am';
      } else if (sessionType === 'pm') {
        if (current === 'pm') delete newSessions[dayNumber];
        else if (current === 'am') newSessions[dayNumber] = 'full_day';
        else if (current === 'full_day') newSessions[dayNumber] = 'am';
        else newSessions[dayNumber] = 'pm';
      }

      setSelectedDays(Object.keys(newSessions).map(Number));
      return newSessions;
    });
  };

  // Fetch data on mount
  const [loadError, setLoadError] = useState(null);
  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // Clear stale section state so navigating from one camp to another never
    // sends the previous camp's section ID to the new camp's add-to-cart endpoint.
    setSelectedSection(null);
    setSections([]);
    try {
      if (type === 'camp') {
        // Fetch camp data
        const response = await api.get(`/camps/${id}/`);
        setItemData(response.data);
        
        // Fetch available sections for this camp
        const sectionsResponse = await api.get(`/camp-sections/?camp=${id}`);
        const sectionsData = sectionsResponse.data?.results || sectionsResponse.data || [];
        setSections(sectionsData);
        
        // Fetch all weekly camps for Jump to Week navigation
        try {
          const allCampsResponse = await api.get('/camps/?limit=50');
          const camps = allCampsResponse.data?.results || allCampsResponse.data || [];
          // Filter only weekly camps (not weekends) and extract week info
          const weeklyCamps = camps.filter(c => c.subcategory !== 'Weekend Classes' && c.name.includes('Week'));
          setAllWeeks(weeklyCamps);
        } catch (err) {
          console.error('Failed to fetch all camps:', err);
        }
      } else if (type === 'course') {
        // Fetch course data
        const response = await api.get(`/courses/${id}/`);
        setItemData(response.data);
        
        // Fetch available sections for this course
        const sectionsResponse = await api.get(`/sections/?course=${id}`);
        const sectionsData = sectionsResponse.data?.results || sectionsResponse.data || [];
        setSections(sectionsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // DON'T auto-redirect home (that's what caused the "Failed to load"
      // + bounce-to-homepage issue on mobile with flaky networks — the error
      // state below lets the parent retry without losing their place).
      const status = error?.response?.status;
      setLoadError({
        status,
        notFound: status === 404,
        message: status === 404
          ? (type === 'camp' ? 'This camp is no longer available.' : 'This course is no longer available.')
          : 'We couldn\'t load this page. Please check your connection and try again.',
      });
    } finally {
      setLoading(false);
    }
  }, [type, id, api]);

  const fetchLearners = useCallback(async () => {
    try {
      const response = await api.get('/learners/');
      const learnersData = response.data?.results || response.data || [];
      setLearners(learnersData);
    } catch (error) {
      console.error('Error fetching learners:', error);
    }
  }, [api]);

  useEffect(() => {
    if (!type || !id) {
      toast.error('Invalid registration link');
      navigate('/');
      return;
    }
    fetchData();
  }, [type, id, fetchData, navigate]);

  useEffect(() => {
    if (user) fetchLearners();
  }, [user, fetchLearners]);

  // Pre-select section if provided
  useEffect(() => {
    if (sectionId && sections.length > 0) {
      const section = sections.find(s => s.id === sectionId);
      if (section) setSelectedSection(section);
    } else if (sections.length === 1) {
      setSelectedSection(sections[0]);
    }
  }, [sectionId, sections]);

  // If the parent switches to a 4-day site (Bednarcik/Plank), drop any
  // Friday (day_number 5) session they may have picked at a 5-day location.
  // Prevents orphaned Friday selections from silently breaking the cart.
  useEffect(() => {
    const is4DaySite =
      selectedSection?.location_name?.includes('Bednarcik') ||
      selectedSection?.location_name?.includes('Plank');
    if (!is4DaySite) return;
    setSelectedSessions(prev => {
      if (!(5 in prev)) return prev;
      const { 5: _dropped, ...rest } = prev;
      return rest;
    });
  }, [selectedSection?.location_name]);

  // Drop any selected session that the admin has cancelled for THIS location.
  // Runs whenever the section (and its resolved schedule) changes, so a
  // parent landing on a location with a mid-week cancellation doesn't carry
  // the cancelled slot into checkout. Backend rejects it anyway, but this
  // keeps the UI honest.
  useEffect(() => {
    const cancelled = new Set(
      (selectedSection?.schedule || [])
        .filter(s => s.cancelled)
        .map(s => `${s.day_number}:${s.session_type}`),
    );
    if (cancelled.size === 0) return;
    setSelectedSessions(prev => {
      let changed = false;
      const next = { ...prev };
      for (const [dn, sess] of Object.entries(prev)) {
        const isBlocked =
          cancelled.has(`${dn}:${sess}`)
          || (sess === 'full_day' && (cancelled.has(`${dn}:am`) || cancelled.has(`${dn}:pm`)));
        if (isBlocked) {
          delete next[dn];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection?.id, (selectedSection?.schedule || []).length]);

  // Full-Week preset also needs to honor the 4-day cap.
  useEffect(() => {
    if (type !== 'camp' || !itemData?.days || !selectedSection) return;
    const preset = searchParams.get('preset');
    if (preset !== 'full_week') return;
    const is4DaySite =
      selectedSection.location_name?.includes('Bednarcik') ||
      selectedSection.location_name?.includes('Plank');
    if (!is4DaySite) return;
    setSelectedSessions(prev => {
      if (!(5 in prev)) return prev;
      const { 5: _drop, ...rest } = prev;
      return rest;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection?.location_name]);

  // Pre-select day for Explorer camps & preset ladders (full_week / full_day / half_day)
  useEffect(() => {
    if (type !== 'camp' || !itemData?.days) return;

    const preset = searchParams.get('preset'); // 'full_week' | 'full_day' | 'half_day'
    const presetSession = searchParams.get('session'); // 'am' | 'pm'

    // 1) Full-Week preset — auto-select all days as full_day (highest conversion nudge).
    //    Respects 4-day SD 308 sites (Bednarcik/Plank) by capping at day 4.
    if (preset === 'full_week') {
      const is4DaySite =
        selectedSection?.location_name?.includes('Bednarcik') ||
        selectedSection?.location_name?.includes('Plank');
      const maxDay = is4DaySite ? 4 : 5;
      const dayNumbers = Array.from(new Set(itemData.days.map(d => d.day_number)))
        .filter(n => n >= 1 && n <= maxDay);
      const sessions = {};
      dayNumbers.forEach(n => { sessions[n] = 'full_day'; });
      setSelectedSessions(sessions);
      setSelectedDays(dayNumbers);
      return;
    }

    // 2) Specific day + optional session (from an AM/PM course card click)
    if (preSelectedDay) {
      const dayNum = parseInt(preSelectedDay);
      if (dayNum >= 1 && dayNum <= 5) {
        if (presetSession === 'am' || presetSession === 'pm') {
          setSelectedSessions({ [dayNum]: presetSession });
        }
        setSelectedDays([dayNum]);
      }
    }
    // 3) For 'full_day' / 'half_day' presets we intentionally DO NOT pre-select
    //    anything — the parent should pick which days they want at that tier.
    //    We just land them on the schedule selector.
  }, [preSelectedDay, type, searchParams, itemData]);


  const handleAddToCart = async () => {
    if (user && !selectedLearner) {
      toast.error('Please select a learner');
      return;
    }

    if (type === 'camp' && Object.keys(selectedSessions).length === 0) {
      toast.error('Please select at least one session');
      return;
    }

    if (!selectedSection) {
      toast.error('Please select a session/location');
      return;
    }

    const isSd308Section = selectedSection.location_eligibility === 'district_only';
    if (isSd308Section) {
      if (!sd308School) {
        toast.error('Please select your student\u2019s school to continue');
        return;
      }
      if (sd308School === '__NOT_LISTED__') {
        toast.error('These camps are reserved for SD 308 students. Please choose a Naperville or Aurora location instead.');
        return;
      }
    }

    // Phone verification gate (Twilio Verify) — after validation, before checkout.
    // Skip if user already verified (persisted) OR verified/skipped in this session.
    const phoneAlreadyVerified = user?.phone_verified === true;
    const phoneAlreadySkipped = user?.phone_verification_skipped === true;
    const phoneToVerify = user?.phone;
    const needsVerification = !!phoneToVerify
      && !phoneAlreadyVerified
      && !phoneVerifiedThisSession
      && !phoneAlreadySkipped
      && !phoneSkippedThisSession;

    if (needsVerification) {
      setShowPhoneVerify(true);
      return;
    }

    setSubmitting(true);
    try {
      if (type === 'camp') {
        await handleCampRegistration();
      } else {
        await handleCourseRegistration();
      }
    } catch (error) {
      console.error('Registration error:', error);
      const apiMsg = error.response?.data?.detail || error.response?.data?.error;
      toast.error(apiMsg || 'Failed to add to cart');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoneVerified = async () => {
    setShowPhoneVerify(false);
    setPhoneVerifiedThisSession(true);
    // If logged in, refresh so the DB-persisted `phone_verified` flag shows up
    // and the gate is permanently cleared next time.
    if (user && refreshUser) {
      try { await refreshUser(); } catch {}
    }
    // Dismiss the modal's internal toast so the add-to-cart toast below
    // becomes the primary message the user sees after landing on /cart.
    toast.dismiss();
    // Let a useEffect resume the cart flow on the next render — this avoids
    // the stale-closure hang that setTimeout(handleAddToCart) used to cause.
    setResumeCheckoutAfterVerify(true);
  };

  const handlePhoneSkipped = () => {
    setShowPhoneVerify(false);
    setPhoneSkippedThisSession(true);
    if (user && refreshUser) {
      try { refreshUser(); } catch {}
    }
    toast.dismiss();
    setResumeCheckoutAfterVerify(true);
  };

  // Resume the add-to-cart flow once the verify/skip state has committed.
  // Running inside a useEffect guarantees handleAddToCart sees the latest
  // phoneVerifiedThisSession / phoneSkippedThisSession values.
  useEffect(() => {
    if (!resumeCheckoutAfterVerify) return;
    if (!phoneVerifiedThisSession && !phoneSkippedThisSession) return;
    setResumeCheckoutAfterVerify(false);
    handleAddToCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeCheckoutAfterVerify, phoneVerifiedThisSession, phoneSkippedThisSession]);


  const handleCampRegistration = async () => {
    // Convert selectedSessions object to array format: [{day: 1, session: 'am'}, ...]
    const selected_days = Object.entries(selectedSessions).map(([day, session]) => ({
      day: parseInt(day),
      session: session
    }));
    
    const payload = {
      camp_section_id: selectedSection.id,
      learner_id: user ? selectedLearner : null,
      selected_days: selected_days,
      extended_care: extendedCare,
      coupon_code: couponApplied?.code || undefined,
      // SD 308 school — only sent when registering at a restricted (Bednarcik/Plank) section.
      ...(((selectedSection.location_name || '').includes('Bednarcik') || (selectedSection.location_name || '').includes('Plank')) && sd308School ? { sd308_school: sd308School } : {})
    };

    await api.post(`/camps/${id}/add-to-cart/`, payload);

    // GA4 Ecommerce — add_to_cart for the just-added camp session(s).
    // Each AM/PM = 1 unit, full_day = 2 units for revenue attribution.
    try {
      const totalUnits = Object.values(selectedSessions).reduce(
        (n, v) => n + (v === 'full_day' ? 2 : 1),
        0,
      );
      const sessionsCount = Object.keys(selectedSessions).length || 1;
      const perUnitPrice = pricing.final && totalUnits
        ? Number((pricing.final / totalUnits).toFixed(2))
        : Number(pricing.final || 0);
      trackAddToCart(
        {
          id: itemData?.id || id,
          name: itemData?.name || itemData?.title,
          category: 'summer_camp',
          location: selectedSection?.location_name || null,
          grade_band: itemData?.grade_bands?.[0] || itemData?.grade_band,
          price: perUnitPrice,
        },
        { quantity: totalUnits || sessionsCount },
      );
    } catch { /* analytics never breaks UX */ }

    // Snapshot what we just added BEFORE refresh so the inline banner can
    // surface a clear "✓ N sessions added · $X" message even after the
    // schedule grid resets.
    const justAddedCount = Object.keys(selectedSessions).length;
    const justAddedTotal = pricing.final;

    // Refresh cart THEN navigate. Doing both before the toast eliminates a
    // 2.5s race that previously caused guest carts to appear empty: during
    // the gap the CartProvider could re-fetch with stale state, or the
    // Django session cookie could be momentarily absent on a parallel request,
    // so the user landed on /cart before the new state propagated.
    await refreshCart();
    toast.success('Added to cart!', { duration: 2500 });
    setLastAdded({ count: justAddedCount, total: justAddedTotal });
    setShowAddedConfirm(true);
  };

  const handleCourseRegistration = async () => {
    const payload = {
      section_id: selectedSection.id,
      learner_id: user ? selectedLearner : null,
      coupon_code: couponApplied?.code || undefined,
    };

    await api.post('/cart/items', payload);

    // GA4 Ecommerce — add_to_cart for the After-School / weekend course.
    try {
      trackAddToCart(
        {
          id: selectedSection?.id,
          name: selectedSection?.section_title || itemData?.name || itemData?.title,
          category: itemData?.category || 'After-School Program',
          location: selectedSection?.location_name || null,
          price: Number(selectedSection?.price || itemData?.price || 0),
        },
      );
    } catch { /* analytics never breaks UX */ }

    await refreshCart();
    toast.success('Added to cart!', { duration: 2500 });
    setShowAddedConfirm(true);
  };

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) { toast.error('Enter a coupon code'); return; }
    // Use current preview subtotal so backend can enforce min_order_amount etc.
    const preview = calculatePrice();
    const subtotal = String(preview.base + (preview.extendedCarePrice || 0) || 0);
    try {
      const res = await api.post('/coupons/validate/', { code, subtotal });
      const data = res.data || {};
      setCouponApplied({
        code: data.code || code,
        label: data.label || code,
        discount_type: data.discount_type,
        discount_value: Number(data.discount_value || 0),
      });
      toast.success(`Coupon applied: ${data.label || code}`);
    } catch (err) {
      const payload = err.response?.data || {};
      toast.error(payload.error_message || payload.detail || 'Invalid coupon code');
    }
  };

  // Calculate pricing using the shared tiered-per-session model.
  // See /app/frontend/src/utils/campPricing.js + backend pricing_resolver.py.
  const calculatePrice = () => {
    if (!selectedSection) return { base: 0, regularSubtotal: 0, discount: 0, final: 0, bundleSavings: 0, session_count: 0, active_tier: null, tiers: [] };

    if (type === 'camp') {
      const res = computePricing({
        selectedSessions,
        section: selectedSection,
        gradeBands: itemData?.grade_bands || [],
        extendedCare,
        coupon: couponApplied,
      });
      return res;
    }

    // Courses (non-camp) retain flat pricing.
    const basePrice = parseFloat(selectedSection.price || 0);
    let discountAmount = 0;
    if (couponApplied && typeof couponApplied === 'object') {
      if (couponApplied.discount_type === 'percent') {
        discountAmount = Math.round(basePrice * Number(couponApplied.discount_value || 0)) / 100;
      } else if (couponApplied.discount_type === 'flat') {
        discountAmount = Math.min(Number(couponApplied.discount_value || 0), basePrice);
      }
    }
    return {
      base: basePrice,
      regularSubtotal: basePrice,
      discount: discountAmount,
      final: basePrice - discountAmount,
      bundleSavings: 0,
      session_count: 0,
      active_tier: null,
      tiers: [],
    };
  };

  const pricing = calculatePrice();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-rsc-navy via-rsc-navy to-rsc-navy/95">
        <Card className="w-full max-w-md bg-white/95 backdrop-blur" data-testid="register-load-error">
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 mx-auto mb-4 flex items-center justify-center">
              <Info className="h-7 w-7 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold mb-2 text-rsc-navy">
              {loadError.notFound ? 'Camp not available' : 'Couldn\'t load this page'}
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              {loadError.message}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              {!loadError.notFound && (
                <Button
                  onClick={fetchData}
                  className="flex-1"
                  data-testid="register-retry-btn"
                >
                  <Loader2 className="h-4 w-4 mr-2 hidden" />
                  Try again
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate('/summer-camps')}
                className="flex-1"
                data-testid="register-browse-btn"
              >
                Browse all camps
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!itemData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Not Found</h2>
          <Button onClick={() => navigate('/summer-camps')}>Browse Camps</Button>
        </div>
      </div>
    );
  }

  const category = CATEGORY_CONFIG[itemData.category] || { label: '', color: 'bg-gray-500' };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rsc-navy via-rsc-navy to-rsc-navy/95 py-8">
      <div className="container-app max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2 text-white/70 hover:text-white hover:bg-white/10">
            <ArrowLeft className="h-4 w-4" />
            Back to {type === 'camp' ? 'Camps' : 'Courses'}
          </Button>
        </div>

        {/* Sign-in prompt for guests */}
        {!user && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between gap-4">
            <p className="text-sm text-gray-700">
              Already have an account?{' '}
              <button
                onClick={() => {
                  const currentUrl = `/register?type=${type}&id=${id}${sectionId ? `&section=${sectionId}` : ''}${preSelectedDay ? `&day=${preSelectedDay}` : ''}`;
                  navigate(`/login?redirect=${encodeURIComponent(currentUrl)}`);
                }}
                className="text-blue-600 hover:text-blue-700 font-medium underline"
              >
                Sign in here
              </button>
              {' '}— or just select your sessions below and check out as a guest.
            </p>
          </div>
        )}

        {/* Logged in banner */}
        {user && (
          <div className="mb-6 flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-700">Logged in as {user.email}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Course/Camp Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-rsc-navy/60 border-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                {/* Image */}
                {itemData.image_url && (
                  <div className="aspect-video rounded-lg overflow-hidden mb-6">
                    <img 
                      src={itemData.image_url} 
                      alt={itemData.name || itemData.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Title & Badge */}
                <div className="space-y-4">
                  <div>
                    <Badge className={`${category.color} text-white mb-3`}>
                      {category.label}
                    </Badge>
                    <h1 className="text-3xl font-bold text-white">
                      {itemData.name || itemData.title}
                    </h1>
                  </div>

                  {/* Description */}
                  {itemData.description && (
                    <p className="text-white/80 leading-relaxed">
                      {itemData.description}
                    </p>
                  )}

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                    {itemData.grade_bands && (
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-rsc-yellow" />
                        <span className="text-rsc-yellow font-medium">
                          Grades: {itemData.grade_bands.join(', ')}
                        </span>
                      </div>
                    )}
                    {type === 'camp' && itemData.days && (
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-4 w-4 text-rsc-yellow" />
                        <span className="text-rsc-yellow font-medium">
                          {(() => {
                            const is4DaySite =
                              selectedSection?.location_name?.includes('Bednarcik') ||
                              selectedSection?.location_name?.includes('Plank');
                            const available = Array.from(new Set(itemData.days.map(d => d.day_number)))
                              .filter(n => !(is4DaySite && n > 4)).length;
                            return `${available} Days`;
                          })()}
                        </span>
                      </div>
                    )}
                    {selectedSection && selectedSection.start_time && selectedSection.end_time && (
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-rsc-yellow" />
                        <span className="text-rsc-yellow font-medium">
                          {new Date(`2000-01-01T${selectedSection.start_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - {new Date(`2000-01-01T${selectedSection.end_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </span>
                      </div>
                    )}
                    {selectedSection && selectedSection.location_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-rsc-yellow" />
                        <span className="text-rsc-yellow font-medium">
                          {selectedSection.location_name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Daily Curriculum - For Camps */}
                {type === 'camp' && itemData.days && itemData.days.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    {/* Post-add-to-cart success banner — explicitly clarifies that
                        the right-sidebar's "$0" total is the NEXT selection,
                        not the cart. Stays visible until parent navigates away. */}
                    {lastAdded && (
                      <div
                        className="mb-4 flex items-center justify-between gap-3 p-3 rounded-lg bg-emerald-500/15 border border-emerald-400/30"
                        data-testid="last-added-banner"
                      >
                        <div className="flex items-center gap-2 text-emerald-100">
                          <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0" />
                          <p className="text-sm">
                            <span className="font-bold">
                              In cart: {itemCount} item{itemCount !== 1 ? 's' : ''} · ${(Number(cart.total) || 0).toFixed(2)}
                            </span>
                            <span className="hidden sm:inline text-emerald-200/80">
                              {' '}— pick more sessions for this week below, or jump to another week.
                            </span>
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate('/cart')}
                          className="text-emerald-100 hover:text-white hover:bg-emerald-500/20 flex-shrink-0"
                          data-testid="last-added-view-cart-btn"
                        >
                          Pay Now
                          <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </div>
                    )}
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-rsc-yellow" />
                      Select Your Schedule
                    </h2>
                    <p className="text-sm text-white/70 mb-4">
                      Check AM or PM per day — or both for a full day. Mix and match to fit your schedule!
                    </p>
                    <div className="space-y-4">
                      {/* Group days by day_number to show each day once with AM/PM options.
                          Bednarcik and Plank are 4-day SD 308 sites (Mon–Thu) — skip
                          day_number 5 (Friday) entirely at those locations so parents
                          never see a tile they can't actually book. */}
                      {(() => {
                        const is4DaySite =
                          selectedSection?.location_name?.includes('Bednarcik') ||
                          selectedSection?.location_name?.includes('Plank');
                        return Array.from(new Set(itemData.days.map(d => d.day_number)))
                          .sort((a, b) => a - b)
                          .filter(dn => !(is4DaySite && dn > 4));
                      })()
                        .map((dayNum) => {
                        const dayData = itemData.days.filter(d => d.day_number === dayNum);
                        const amDay = dayData.find(d => d.session_type === 'am');
                        const pmDay = dayData.find(d => d.session_type === 'pm');
                        
                        const selectedSession = selectedSessions[dayNum];
                        
                        // Calculate the actual date for this day
                        let dateString = '';
                        let dayName = `Day ${dayNum}`;
                        
                        if (selectedSection?.start_date) {
                          // Parse date string as local date to avoid timezone issues
                          const [yearVal, monthVal, dayVal] = selectedSection.start_date.split('-').map(Number);
                          const startDate = new Date(yearVal, monthVal - 1, dayVal); // month is 0-indexed
                          const currentDate = new Date(startDate);
                          currentDate.setDate(startDate.getDate() + (dayNum - 1));
                          
                          // Get day of week from actual date
                          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                          dayName = dayNames[currentDate.getDay()];
                          
                          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                                            'July', 'August', 'September', 'October', 'November', 'December'];
                          const month = monthNames[currentDate.getMonth()];
                          const day = currentDate.getDate();
                          const year = currentDate.getFullYear();
                          
                          // Add ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
                          const ordinal = (d) => {
                            if (d > 3 && d < 21) return 'th';
                            switch (d % 10) {
                              case 1: return 'st';
                              case 2: return 'nd';
                              case 3: return 'rd';
                              default: return 'th';
                            }
                          };
                          
                          dateString = `${month} ${day}${ordinal(day)}, ${year}`;
                        }
                        
                        // Use the course info from AM session for display (or PM if AM doesn't exist)
                        const displayDay = amDay || pmDay;
                        if (!displayDay) return null;
                        
                        // Get all unique categories for this day
                        const categories = Array.from(new Set(
                          dayData
                            .map(d => d.course_category)
                            .filter(Boolean)
                            .map(cat => cat.charAt(0).toUpperCase() + cat.slice(1))
                        )).join(' & ');
                        
                        const amPrice = selectedSection?.price_am_session || 69;
                        const pmPrice = selectedSection?.price_pm_session || 69;
                        const fullDayPrice = selectedSection?.price_per_day || 119;
                        // K-2 camps are structured as half-day AM only. Hiding
                        // the Afternoon and Full Day tiles for these camps
                        // prevents parents from attempting an enrollment the
                        // program can't actually deliver and matches the
                        // grade-filter UX on /summer-camps.
                        const isK2Camp = (itemData?.grade_bands || []).includes('K-2');

                        // Per-location override map — admin may have cancelled
                        // AM/PM/Full-Day at this section only, or swapped the
                        // course. `schedule` ships from the API resolver.
                        const overrides = (selectedSection?.schedule || [])
                          .filter(s => s.day_number === dayNum);
                        const amOverride = overrides.find(s => s.session_type === 'am');
                        const pmOverride = overrides.find(s => s.session_type === 'pm');
                        const amCancelled = !!amOverride?.cancelled;
                        const pmCancelled = !!pmOverride?.cancelled;
                        const fullDayCancelled = amCancelled || pmCancelled;
                        // If every bookable session on this day is cancelled,
                        // skip the whole day tile — there's nothing to book.
                        if (
                          (isK2Camp && amCancelled)
                          || (!isK2Camp && amCancelled && pmCancelled)
                        ) {
                          return (
                            <div
                              key={dayNum}
                              className="rounded-lg border-2 border-red-400/30 bg-red-950/30 p-4 text-center"
                              data-testid={`day-cancelled-${dayNum}`}
                            >
                              <div className="text-xs font-bold text-red-200 uppercase tracking-wide">
                                {dayName}{dateString ? ` · ${dateString}` : ''}
                              </div>
                              <div className="text-sm text-red-100/80 mt-1">
                                Cancelled at {selectedSection?.location_name}
                                {amOverride?.override_notes ? ` — ${amOverride.override_notes}` : ''}
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                          <div key={dayNum} className="rounded-lg border-2 border-white/10 bg-gradient-to-br from-rsc-navy/40 to-rsc-navy/60 backdrop-blur-sm overflow-hidden">
                            {/* Themed day header — STEM rotation gradient to match /summer-camps. */}
                            {(() => {
                              const theme = DAY_THEME_REG[parseInt(dayNum)] || DAY_THEME_REG[1];
                              const ThemeIcon = theme.Icon;
                              return (
                                <div className={`px-4 py-2.5 bg-gradient-to-r ${theme.gradient} relative overflow-hidden`}>
                                  <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 1px, transparent 1px), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '20px 20px, 30px 30px' }} />
                                  <div className="relative flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="text-[10px] font-bold text-white/80 uppercase tracking-[0.15em] leading-none">{dayName}</div>
                                      <div className="flex items-center gap-1.5 mt-1">
                                        <ThemeIcon className="w-4 h-4 text-white flex-shrink-0" />
                                        <span className="text-sm font-extrabold text-white tracking-wide leading-none">{theme.label}</span>
                                      </div>
                                    </div>
                                    {dateString && (
                                      <span className="text-[11px] font-semibold text-white/90 whitespace-nowrap">{dateString}</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                            <div className="p-4">
                            {/* Session Selector Buttons with course titles inside.
                                K-2 camps are AM-only, so we render a single
                                full-width Morning tile instead of the 3-column
                                AM / PM / Full-Day grid. */}
                            <div className={isK2Camp ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-1 sm:grid-cols-2 gap-2'}>
                              {/* Morning Button */}
                              {(() => {
                                const isAmSelected = selectedSession === 'am' || selectedSession === 'full_day';
                                const amTitle = amDay ? amDay.course_title.replace(/^W\d+\s+\w+\s+AM:\s*/, '') : '';
                                return (
                              <button
                                type="button"
                                disabled={amCancelled}
                                onClick={() => { if (!amCancelled) toggleSession(dayNum, 'am'); }}
                                data-testid={`day-select-am-${dayNum}`}
                                aria-checked={isAmSelected}
                                role="checkbox"
                                className={`p-4 rounded-lg border-2 transition-all text-left relative ${
                                  amCancelled
                                    ? 'border-red-400/40 bg-red-950/30 opacity-60 cursor-not-allowed'
                                    : isAmSelected
                                    ? 'border-blue-400 bg-blue-500/30 shadow-lg shadow-blue-500/20'
                                    : 'border-white/20 hover:border-white/40 hover:bg-white/5'
                                }`}
                              >
                                <div className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded border-2 pointer-events-none"
                                     style={{ borderColor: isAmSelected ? '#60a5fa' : 'rgba(255,255,255,0.35)', backgroundColor: isAmSelected ? '#60a5fa' : 'transparent' }}>
                                  {isAmSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                                </div>
                                {amCancelled && (
                                  <span className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white uppercase tracking-wider" data-testid={`am-cancelled-${dayNum}`}>Cancelled</span>
                                )}
                                <div className={`font-semibold text-sm mb-1 ${isAmSelected ? 'text-blue-200' : 'text-white'} pr-6`}>
                                  Morning
                                </div>
                                <div className="text-xs text-white/60 mb-3">{amDay?.start_time && amDay?.end_time ? `${formatTime(amDay.start_time)}–${formatTime(amDay.end_time)}` : '9 AM – 12 PM'}</div>
                                {amDay && (
                                  <>
                                    {amDay.thumbnail_url && (
                                      <div
                                        onClick={(e) => { e.stopPropagation(); courseDetailModal.openCourseDetail(amTitle); }}
                                        className="relative w-full aspect-[4/3] rounded-md overflow-hidden mb-2 cursor-pointer ring-0 hover:ring-2 hover:ring-rsc-yellow/60 transition-all group/thumb"
                                        title={`View details for ${amTitle}`}
                                        data-testid={`reg-course-thumb-am-${dayNum}`}
                                      >
                                        <img src={amDay.thumbnail_url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover/thumb:scale-105 transition-transform duration-300" onError={(e) => { e.target.style.display = 'none'; }} />
                                      </div>
                                    )}
                                    <div
                                      onClick={(e) => { e.stopPropagation(); courseDetailModal.openCourseDetail(amTitle); }}
                                      className={`text-sm font-bold mb-1 leading-tight hover:underline cursor-pointer ${isAmSelected ? 'text-rsc-yellow' : 'text-rsc-yellow/90'}`}
                                    >
                                      {amTitle}
                                    </div>
                                    {/* Always-visible short course description — Feb 2026 per PDF feedback.
                                        Capped at 110 chars so long descriptions don't break the card height.
                                        The "More Details" link opens the full modal with outcomes + kit. */}
                                    {(amDay.course_description || amDay.description) && (
                                      <p className="text-[11px] text-white/70 leading-snug mb-1" data-testid={`reg-course-blurb-am-${dayNum}`}>
                                        {(() => {
                                          const full = amDay.course_description || amDay.description || '';
                                          return full.length > 110 ? `${full.slice(0, 110).trim()}…` : full;
                                        })()}
                                      </p>
                                    )}
                                    <div
                                      onClick={(e) => { e.stopPropagation(); courseDetailModal.openCourseDetail(amTitle); }}
                                      className="text-xs text-rsc-yellow hover:text-rsc-yellow-bright underline mt-1 transition-colors cursor-pointer"
                                    >
                                      More Details →
                                    </div>
                                  </>
                                )}
                                <div className={`text-sm font-bold ${isAmSelected ? 'text-blue-300' : 'text-white/90'} mt-2`}>
                                  ${amPrice}
                                </div>
                              </button>
                                );
                              })()}

                              {/* Afternoon — hidden for K-2 camps (AM-only). Full Day tile REMOVED in Feb 2026: AM+PM checked = full day auto-applied. */}
                              {!isK2Camp && (() => {
                                const isPmSelected = selectedSession === 'pm' || selectedSession === 'full_day';
                                const pmTitle = pmDay ? pmDay.course_title.replace(/^W\d+\s+\w+\s+PM:\s*/, '') : '';
                                return (
                              <button
                                type="button"
                                disabled={pmCancelled}
                                onClick={() => { if (!pmCancelled) toggleSession(dayNum, 'pm'); }}
                                data-testid={`day-select-pm-${dayNum}`}
                                aria-checked={isPmSelected}
                                role="checkbox"
                                className={`p-4 rounded-lg border-2 transition-all text-left relative ${
                                  pmCancelled
                                    ? 'border-red-400/40 bg-red-950/30 opacity-60 cursor-not-allowed'
                                    : isPmSelected
                                    ? 'border-blue-400 bg-blue-500/30 shadow-lg shadow-blue-500/20'
                                    : 'border-white/20 hover:border-white/40 hover:bg-white/5'
                                }`}
                              >
                                <div className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded border-2 pointer-events-none"
                                     style={{ borderColor: isPmSelected ? '#60a5fa' : 'rgba(255,255,255,0.35)', backgroundColor: isPmSelected ? '#60a5fa' : 'transparent' }}>
                                  {isPmSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                                </div>
                                {pmCancelled && (
                                  <span className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white uppercase tracking-wider" data-testid={`pm-cancelled-${dayNum}`}>Cancelled</span>
                                )}
                                <div className={`font-semibold text-sm mb-1 ${isPmSelected ? 'text-blue-200' : 'text-white'} pr-6`}>
                                  Afternoon
                                </div>
                                <div className="text-xs text-white/60 mb-3">{pmDay?.start_time && pmDay?.end_time ? `${formatTime(pmDay.start_time)}–${formatTime(pmDay.end_time)}` : '1 PM – 4 PM'}</div>
                                {pmDay && (
                                  <>
                                    {pmDay.thumbnail_url && (
                                      <div
                                        onClick={(e) => { e.stopPropagation(); courseDetailModal.openCourseDetail(pmTitle); }}
                                        className="relative w-full aspect-[4/3] rounded-md overflow-hidden mb-2 cursor-pointer ring-0 hover:ring-2 hover:ring-rsc-yellow/60 transition-all group/thumb"
                                        title={`View details for ${pmTitle}`}
                                        data-testid={`reg-course-thumb-pm-${dayNum}`}
                                      >
                                        <img src={pmDay.thumbnail_url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover/thumb:scale-105 transition-transform duration-300" onError={(e) => { e.target.style.display = 'none'; }} />
                                      </div>
                                    )}
                                    <div
                                      onClick={(e) => { e.stopPropagation(); courseDetailModal.openCourseDetail(pmTitle); }}
                                      className={`text-sm font-bold mb-1 leading-tight hover:underline cursor-pointer ${isPmSelected ? 'text-rsc-yellow' : 'text-rsc-yellow/90'}`}
                                    >
                                      {pmTitle}
                                    </div>
                                    {(pmDay.course_description || pmDay.description) && (
                                      <p className="text-[11px] text-white/70 leading-snug mb-1" data-testid={`reg-course-blurb-pm-${dayNum}`}>
                                        {(() => {
                                          const full = pmDay.course_description || pmDay.description || '';
                                          return full.length > 110 ? `${full.slice(0, 110).trim()}…` : full;
                                        })()}
                                      </p>
                                    )}
                                    <div
                                      onClick={(e) => { e.stopPropagation(); courseDetailModal.openCourseDetail(pmTitle); }}
                                      className="text-xs text-rsc-yellow hover:text-rsc-yellow-bright underline mt-1 transition-colors cursor-pointer"
                                    >
                                      More Details →
                                    </div>
                                  </>
                                )}
                                <div className={`text-sm font-bold ${isPmSelected ? 'text-blue-300' : 'text-white/90'} mt-2`}>
                                  ${pmPrice}
                                </div>
                              </button>
                                );
                              })()}
                            </div>
                            {/* Full-day savings pill — shown when both AM + PM are selected for this day. */}
                            {selectedSession === 'full_day' && !isK2Camp && (
                              <div className="mt-2 px-3 py-1.5 rounded-md bg-emerald-500/20 border border-emerald-400/40 text-xs font-semibold text-emerald-200 flex items-center gap-2" data-testid={`full-day-pill-${dayNum}`}>
                                <Check className="w-3.5 h-3.5" strokeWidth={3} />
                                Full day booked (9 AM – 4 PM · lunch 12–1 PM) · tier pricing applied
                              </div>
                            )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {Object.keys(selectedSessions).length > 0 && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800 font-medium mb-2">
                          {Object.keys(selectedSessions).length} session{Object.keys(selectedSessions).length !== 1 ? 's' : ''} selected
                        </p>
                        <div className="space-y-1">
                          {Object.entries(selectedSessions).sort(([a], [b]) => a - b).map(([day, session]) => (
                            <div key={day} className="text-xs text-blue-700">
                              • {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'][day-1]}: {
                                session === 'am' ? 'Morning (9-12)' : 
                                session === 'pm' ? 'Afternoon (1-4)' : 
                                'Full Day (9-4)'
                              }
                            </div>
                          ))}
                        </div>
                        {pricing.bundleSavings > 0 && (
                          <div className="mt-3 pt-3 border-t border-blue-300">
                            <p className="text-sm text-emerald-700 font-semibold">
                              💡 You're saving ${pricing.bundleSavings.toFixed(2)} with the weekly bundle!
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* What to Bring Section */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5 text-rsc-yellow" />
                    What to Bring
                  </h2>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-white">Lunch & Snacks</p>
                        <p className="text-sm text-white/80">Pack a nutritious lunch and 2-3 snacks for the day</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-white">Water Bottle</p>
                        <p className="text-sm text-white/80">Stay hydrated! We have refill stations available</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-white">Curiosity & Enthusiasm</p>
                        <p className="text-sm text-white/80">Ready to explore, create, and have fun!</p>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-rsc-yellow/20 border border-rsc-yellow/40 rounded-lg">
                      <p className="text-sm text-rsc-yellow">
                        <strong>Note:</strong> All STEM materials, computers, and robotics kits are provided. No need to bring technology!
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Continue shopping button - mobile */}
            <div className="lg:hidden">
              <Button 
                variant="outline" 
                onClick={() => navigate(-1)}
                className="w-full"
              >
                Continue Shopping
              </Button>
            </div>
          </div>

          {/* Right Column - Registration Form */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8 bg-white/95 backdrop-blur-sm border-white/20">
              <CardHeader className="border-b border-gray-200">
                <CardTitle className="text-gray-900">Registration</CardTitle>
                {lastAdded && itemCount > 0 && (
                  <p className="text-[11px] text-emerald-700 font-semibold mt-1" data-testid="sidebar-cart-hint">
                    Cart: {itemCount} item{itemCount !== 1 ? 's' : ''} · ${(Number(cart.total) || 0).toFixed(2)} · this panel = your next selection
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-6 text-gray-900">
                
                {/* Learner Selection (or empty-state CTA) */}
                {user && learners.length > 0 && (
                  <div>
                    <Label>Select Learner *</Label>
                    <Select value={selectedLearner} onValueChange={setSelectedLearner}>
                      <SelectTrigger data-testid="select-learner">
                        <SelectValue placeholder="Choose a learner" />
                      </SelectTrigger>
                      <SelectContent>
                        {learners.map(learner => (
                          <SelectItem key={learner.id} value={learner.id}>
                            {learner.first_name} {learner.last_name} (Grade {learner.grade})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Link to="/parent/learners" className="text-xs text-blue-600 hover:underline mt-1 inline-block">
                      + Add new learner
                    </Link>
                  </div>
                )}

                {/* No-learners empty state — Feb 2026 per user feedback.
                    Logged-in parents who haven't added a learner yet were
                    previously shown nothing, not even a CTA, until they
                    found "Manage Learners" in the dashboard sidebar. Now
                    we surface a prominent inline button that takes them
                    straight to /parent/learners. */}
                {user && learners.length === 0 && (
                  <div className="rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 p-4 text-center" data-testid="no-learners-cta">
                    <p className="text-sm font-medium text-blue-900 mb-1">No learners on your account yet</p>
                    <p className="text-xs text-blue-700/80 mb-3">Add your child's name, grade, and date of birth to register them for camp.</p>
                    <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-first-learner-btn">
                      <Link to="/parent/learners?from=register">+ Add a learner</Link>
                    </Button>
                  </div>
                )}

                {/* Session/Location Selection */}
                {sections.length > 0 && (
                  <div>
                    <Label>Select Session/Location *</Label>
                    <Select 
                      value={selectedSection?.id} 
                      onValueChange={(val) => setSelectedSection(sections.find(s => s.id === val))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a session" />
                      </SelectTrigger>
                      <SelectContent>
                        {sections.map(section => (
                          <SelectItem key={section.id} value={section.id}>
                            <div className="flex flex-col">
                              <span>{section.location_name || 'Location'}</span>
                              {section.start_date && (
                                <span className="text-xs text-gray-500">
                                  {formatLocalDate(section.start_date, { month: 'numeric', day: 'numeric', year: 'numeric' })} - {formatLocalDate(section.end_date, { month: 'numeric', day: 'numeric', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Day Summary for Camps — read-only mirror of session picks above.
                    Clicking a selected day CLEARS that session. Days with no
                    session chosen are disabled to prevent phantom selections. */}
                {type === 'camp' && selectedSection && itemData?.subcategory !== 'Weekend Classes' && (
                  <div>
                    <Label>Selected Days</Label>
                    {(() => {
                      const is4DayLocation =
                        selectedSection?.location_name?.includes('Bednarcik') ||
                        selectedSection?.location_name?.includes('Plank');
                      // 4-day SD 308 sites show a Mon–Thu grid; RSS sites show Mon–Fri.
                      const visibleDayNames = is4DayLocation
                        ? DAY_NAMES.slice(0, 4)
                        : DAY_NAMES;
                      const gridCols = is4DayLocation ? 'grid-cols-4' : 'grid-cols-5';
                      return (
                        <div className={`grid ${gridCols} gap-2 mt-2`}>
                          {visibleDayNames.map((day, idx) => {
                            const dayNum = idx + 1;
                            const session = selectedSessions[dayNum];
                            const hasSession = !!session;
                            const disabled = !hasSession;
                            const sessionLabel = session === 'full_day' ? 'Full' : session === 'am' ? 'AM' : session === 'pm' ? 'PM' : '';

                            return (
                              <button
                                key={day}
                                type="button"
                                disabled={disabled}
                                title={
                                  hasSession
                                    ? `Click to remove ${day} from your schedule`
                                    : 'Check AM and/or PM in the schedule above to select this day'
                                }
                                onClick={() => {
                                  if (!hasSession) return; // guard against phantom selections
                                  setSelectedSessions(prev => {
                                    const next = { ...prev };
                                    delete next[dayNum];
                                    setSelectedDays(Object.keys(next).map(Number));
                                    return next;
                                  });
                            }}
                            data-testid={`day-pill-${day.toLowerCase()}`}
                            className={`
                              p-2 rounded-lg text-xs font-medium transition-all flex flex-col items-center justify-center gap-0.5
                              ${hasSession ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer' : 'bg-gray-100 text-gray-400'}
                              ${disabled && !hasSession ? 'cursor-not-allowed' : ''}
                            `}
                          >
                            <span>{day}</span>
                            {sessionLabel && <span className="text-[10px] opacity-80">{sessionLabel}</span>}
                          </button>
                        );
                      })}
                        </div>
                      );
                    })()}
                    <p className="text-xs text-gray-500 mt-2">
                      {Object.keys(selectedSessions).length === 0
                        ? 'Check AM and/or PM in the schedule above to add a day.'
                        : `${Object.keys(selectedSessions).length} day(s) selected — click a day to remove it.`}
                    </p>
                  </div>
                )}

                {/* SD 308 school eligibility check — shown only when the selected section
                    is Bednarcik or Plank Junior High (partnership-only locations). */}
                {type === 'camp' && selectedSection && (selectedSection.location_name?.includes('Bednarcik') || selectedSection.location_name?.includes('Plank')) && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="sd308-eligibility-block">
                    <Label className="text-amber-900 font-semibold">
                      Student's School <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-xs text-amber-800 mt-1 mb-2">
                      This location is reserved for Oswego School District 308 students. Please confirm which SD 308 school your student attends.
                    </p>
                    <Select value={sd308School} onValueChange={setSd308School}>
                      <SelectTrigger data-testid="sd308-school-select" className="bg-white">
                        <SelectValue placeholder="Select SD 308 school…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[280px]">
                        {SD308_SCHOOLS.map(name => (
                          <SelectItem key={name} value={name}>{name}</SelectItem>
                        ))}
                        <SelectItem value="__NOT_LISTED__" className="text-red-700 font-semibold border-t mt-1 pt-1">
                          My student is not in SD 308
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {sd308School === '__NOT_LISTED__' && (
                      <p className="text-xs text-red-700 font-semibold mt-2" data-testid="sd308-ineligible-msg">
                        These camps are reserved for SD 308 students. Please pick a Naperville or Aurora location — both are open to everyone.
                      </p>
                    )}
                  </div>
                )}

                {/* Extended Care for Camps */}
                {type === 'camp' && selectedSection && selectedSection.extended_care_weekly && (
                  <div>
                    <Label>Extended Care (Optional)</Label>
                    <Select value={extendedCare} onValueChange={setExtendedCare}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Extended Care</SelectItem>
                        <SelectItem value="early">Early Drop-off (8:00 AM - 9:00 AM)</SelectItem>
                        <SelectItem value="late">Late Pick-up (4:00 PM - 5:00 PM)</SelectItem>
                        <SelectItem value="both">Both Early & Late (8:00 AM - 9:00 AM + 4:00 PM - 5:00 PM)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Coupon Code */}
                <div>
                  <Label>Coupon Code (Optional)</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      disabled={!!couponApplied}
                    />
                    {!couponApplied ? (
                      <Button onClick={applyCoupon} variant="outline" size="sm">
                        Apply
                      </Button>
                    ) : (
                      <Button 
                        onClick={() => { setCouponApplied(null); setCouponCode(''); }}
                        variant="outline" 
                        size="sm"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {couponApplied && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      {couponApplied.label || `Coupon ${couponApplied.code} applied!`}
                    </p>
                  )}
                </div>

                {/* Price Summary */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {type === 'camp' && pricing.session_count > 0 ? (
                        <>
                          {pricing.session_count} session{pricing.session_count !== 1 ? 's' : ''} at top rate
                          <span className="text-gray-400 text-xs ml-1">(${pricing.top_rate}/session)</span>
                        </>
                      ) : (
                        <>Subtotal</>
                      )}
                    </span>
                    <span className="font-medium">${pricing.regularSubtotal.toFixed(2)}</span>
                  </div>
                  {pricing.bundleSavings > 0 && (
                    <div className="flex justify-between text-sm text-emerald-600" data-testid="bundle-discount-line">
                      <span>
                        Multi-session discount
                        {pricing.active_rate != null && (
                          <span className="text-emerald-600/70 text-xs ml-1">(${pricing.active_rate}/session tier)</span>
                        )}
                      </span>
                      <span>-${pricing.bundleSavings.toFixed(2)}</span>
                    </div>
                  )}
                  {pricing.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Coupon Discount (20% off)</span>
                      <span>-${pricing.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total</span>
                    <span className="text-blue-600">${pricing.final.toFixed(2)}</span>
                  </div>
                </div>

                {/* Add to Cart Button */}
                <Button 
                  onClick={handleAddToCart}
                  disabled={
                    submitting ||
                    (user && learners.length > 0 && !selectedLearner) ||
                    (type === 'camp' && Object.keys(selectedSessions).length === 0)
                  }
                  className="w-full gap-2"
                  size="lg"
                  data-testid="add-to-cart-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-4 w-4" />
                      Add to Cart - ${pricing.final.toFixed(2)}
                    </>
                  )}
                </Button>

                {/* Continue shopping - desktop */}
                <Button 
                  variant="ghost" 
                  onClick={() => navigate(-1)}
                  className="w-full hidden lg:flex"
                >
                  Continue Shopping
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Jump to a Week — moved below the form so the parent can jump after committing to (or dismissing) this week's choice */}
        {type === 'camp' && allWeeks.length > 0 && (() => {
          // Derive the current camp's grade band so Jump-to-Week stays in-band.
          // If the current camp is labeled ['3-5', '6-8'] we include both.
          const currentBands = new Set(itemData?.grade_bands || []);
          const bandLabel = currentBands.has('K-2') ? 'K–2'
            : currentBands.has('3-5') && currentBands.has('6-8') ? '3–8'
            : currentBands.has('3-5') ? '3–5'
            : currentBands.has('6-8') ? '6–8'
            : 'All';
          const inBand = (camp) => {
            const bands = camp.grade_bands || [];
            return bands.some(b => currentBands.has(b));
          };
          const inBandWeeks = allWeeks.filter(inBand);
          return (
          <div className="mt-10 p-4 rounded-xl bg-rsc-navy/60 border border-white/10 backdrop-blur-sm" data-testid="jump-to-week">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Calendar className="w-4 h-4 text-rsc-yellow" />
                Jump to a Week
                <span className="text-[10px] font-bold text-rsc-yellow/70 uppercase tracking-wider">Grades {bandLabel}</span>
              </h3>
              {/* Grade-band quick switcher — clicking a different band jumps back to
                  /summer-camps with that filter pre-applied (and that page will also
                  auto-scroll to Week 1 of the new band). */}
              <div className="flex items-center gap-1" data-testid="grade-band-switcher">
                {['K-2', '3-5', '6-8'].map(b => {
                  const active = currentBands.has(b);
                  return (
                    <button
                      key={b}
                      type="button"
                      onClick={() => navigate(`/summer-camps?grade=${b}`)}
                      className={`px-2.5 py-1 text-[11px] font-bold rounded-full border transition-colors ${
                        active
                          ? 'bg-rsc-yellow text-rsc-navy border-rsc-yellow'
                          : 'bg-transparent text-white/70 border-white/15 hover:border-white/35 hover:text-white'
                      }`}
                      data-testid={`grade-switch-${b}`}
                    >
                      {b === 'K-2' ? 'K–2' : b === '3-5' ? '3–5' : '6–8'}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-9 gap-2">
              {Array.from({ length: 8 }, (_, i) => {
                const wn = i + 1;
                const weekDates = ['Jun 8–12','Jun 15–19','Jun 22–26','Jun 29–Jul 3','Jul 13–17','Jul 20–24','Jul 27–31','Aug 3–7'];
                const matchingCamp = inBandWeeks.find(c => c.name.includes(`Week ${wn}`));
                const isCurrentWeek = matchingCamp && matchingCamp.id === itemData?.id;
                return (
                  <button key={wn}
                    onClick={() => {
                      if (matchingCamp && !isCurrentWeek) {
                        navigate(`/register?type=camp&id=${matchingCamp.id}`);
                      }
                    }}
                    className={`p-2 rounded-lg border text-center transition-all text-xs ${
                      isCurrentWeek 
                        ? 'border-rsc-yellow bg-rsc-yellow/20 ring-1 ring-rsc-yellow' 
                        : matchingCamp 
                          ? 'border-rsc-yellow/20 bg-rsc-yellow/5 hover:border-rsc-yellow/40 hover:bg-rsc-yellow/10 cursor-pointer' 
                          : 'border-white/5 opacity-30 cursor-default'
                    }`}
                  >
                    <p className="font-bold text-rsc-yellow">Week {wn}</p>
                    <p className="text-[10px] text-white/40">{weekDates[i]}</p>
                  </button>
                );
              })}
              <div className="p-2 rounded-lg border border-red-500/20 bg-red-500/5 text-center flex flex-col items-center justify-center">
                <p className="text-[10px] font-bold text-red-300">Jul 4</p>
                <p className="text-[9px] text-red-300/50">No camp</p>
              </div>
            </div>
          </div>
          );
        })()}
      </div>

      {/* Sticky checkout bar — keeps the "close the loop" action in view on long scrolls.
          Shows when the user has an active session selection OR already has items in cart. */}
      {type === 'camp' && (Object.keys(selectedSessions).length > 0 || itemCount > 0) && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-rsc-navy/95 backdrop-blur-lg border-t border-rsc-yellow/30 shadow-[0_-8px_24px_rgba(0,0,0,0.3)]"
          data-testid="sticky-checkout-bar"
        >
          <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {Object.keys(selectedSessions).length > 0 ? (
                <>
                  <div className="hidden sm:flex flex-col leading-tight">
                    <span className="text-[11px] font-bold text-white/50 uppercase tracking-wider">
                      {Object.keys(selectedSessions).length} session{Object.keys(selectedSessions).length !== 1 ? 's' : ''} selected
                    </span>
                    {pricing.bundleSavings > 0 && (
                      <span className="text-[11px] text-emerald-400 font-semibold">
                        Saving ${pricing.bundleSavings.toFixed(0)} with full-day bundle
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-[10px] text-white/50">Total</span>
                    <span className="text-2xl font-extrabold text-rsc-yellow">
                      ${pricing.final.toFixed(2)}
                    </span>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/cart')}
                  className="flex items-center gap-2 text-white hover:text-rsc-yellow transition-colors"
                  data-testid="sticky-view-cart-btn"
                >
                  <ShoppingCart className="w-5 h-5 text-rsc-yellow" />
                  <span className="font-bold">
                    {itemCount} item{itemCount !== 1 ? 's' : ''} in cart
                  </span>
                  <span className="text-rsc-yellow font-extrabold">
                    ${(Number(cart.total) || 0).toFixed(2)}
                  </span>
                </button>
              )}
            </div>
            {Object.keys(selectedSessions).length > 0 ? (
              <Button
                onClick={handleAddToCart}
                disabled={
                  submitting ||
                  (user && learners.length > 0 && !selectedLearner) ||
                  (type === 'camp' && Object.keys(selectedSessions).length === 0) ||
                  (type === 'camp' && selectedSection && (selectedSection.location_name?.includes('Bednarcik') || selectedSection.location_name?.includes('Plank')) && (!sd308School || sd308School === '__NOT_LISTED__'))
                }
                size="lg"
                className="bg-rsc-yellow hover:bg-rsc-yellow-bright text-rsc-navy font-extrabold px-6 sm:px-10 rounded-full shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100"
                data-testid="sticky-checkout-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Adding…
                  </>
                ) : (
                  <>
                    Add to Cart
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/cart')}
                size="lg"
                className="bg-rsc-yellow hover:bg-rsc-yellow-bright text-rsc-navy font-extrabold px-6 sm:px-10 rounded-full shadow-lg hover:scale-[1.02] transition-all"
                data-testid="sticky-checkout-now-btn"
              >
                Checkout
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Spacer so the sticky bar never overlaps page content */}
      {type === 'camp' && (Object.keys(selectedSessions).length > 0 || itemCount > 0) && (
        <div className="h-20" aria-hidden="true" />
      )}

      {/* Course Details Modal */}
      <CourseDetailModal 
        state={courseDetailModal.state} 
        onClose={courseDetailModal.close}
      />

      {/* Post-Add-To-Cart Confirmation — nudges parents to bundle multiple weeks
          before being whisked off to /cart. Big lever for multi-week bookings. */}
      <Dialog
        open={showAddedConfirm}
        onOpenChange={setShowAddedConfirm}
      >
        <DialogContent className="max-w-md" data-testid="added-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              Added to your cart!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-muted-foreground">
              Most parents bundle <span className="font-semibold text-rsc-navy">2–3 weeks</span>{' '}
              of camp for the summer. Want to add another week now?
            </p>
            {allWeeks.length > 0 && (() => {
              const currentBands = new Set(itemData?.grade_bands || []);
              const inBand = (camp) => (camp.grade_bands || []).some(b => currentBands.has(b));
              const otherWeeks = allWeeks.filter(c => inBand(c) && c.id !== itemData?.id).slice(0, 3);
              if (!otherWeeks.length) return null;
              return (
                <div className="space-y-1.5 pt-1" data-testid="suggest-other-weeks">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    Quick add another week
                  </p>
                  {otherWeeks.map(c => {
                    const m = c.name.match(/Week\s+(\d)/i);
                    const num = m ? m[1] : '?';
                    const dates = ['Jun 8–12','Jun 15–19','Jun 22–26','Jun 29–Jul 3','Jul 13–17','Jul 20–24','Jul 27–31','Aug 3–7'][Number(num) - 1] || '';
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setShowAddedConfirm(false);
                          navigate(`/register?type=camp&id=${c.id}`);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-border hover:border-rsc-blue hover:bg-rsc-blue/5 transition-colors text-left"
                        data-testid={`suggest-week-${num}`}
                      >
                        <span className="text-sm font-semibold">Week {num}</span>
                        <span className="text-xs text-muted-foreground">{dates}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-rsc-blue" />
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-3">
            <Button
              variant="ghost"
              onClick={() => setShowAddedConfirm(false)}
              data-testid="add-more-this-week-btn"
            >
              + More this week
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddedConfirm(false);
                // Navigate back to /summer-camps so parent picks a DIFFERENT week
                // — staying on this same week's /register page would leave them
                // confused with a $0 reset state. Grade filter is preserved.
                const band = (itemData?.grade_bands || [])[0];
                navigate(band ? `/summer-camps?grade=${band}` : '/summer-camps');
              }}
              data-testid="add-another-week-btn"
            >
              Pick another week
            </Button>
            <Button
              onClick={() => {
                setShowAddedConfirm(false);
                navigate('/cart');
              }}
              data-testid="view-cart-btn"
            >
              Pay Now
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phone Verification Modal (Twilio Verify) */}
      <PhoneVerificationModal
        open={showPhoneVerify}
        phone={user?.phone}
        purpose="registration"
        allowSkip={true}
        autoSend={false}
        authToken={localStorage.getItem('token')}
        title="Verify Your Phone Number"
        description="We text class reminders and urgent updates. A verified number means no missed messages."
        onVerified={handlePhoneVerified}
        onSkip={handlePhoneSkipped}
        onClose={() => setShowPhoneVerify(false)}
      />

    </div>
  );
}
